// User-nominated "great roads". The road equivalent of api/nominations.ts:
// a signed-in user nominates a road they love (name + a point on it + a one-line
// "why"); it then surfaces for everyone via the same curated-road matcher that
// powers Round Hill Road. Votes promote a nomination's prominence over time.
//
// Geometry note: a nomination is SUBMITTED as a single representative point.
// When Morgan approves it in the admin dashboard, the approve step resolves
// the road's real geometry from OSM and stores it in `samples` (ordered
// [lng, lat] pairs, the greatDrives.data.json shape), so the matcher can
// measure actual miles on the road. Rows approved before Sep 2026 have no
// samples and fall back to the point.

import { supabase } from "@/integrations/supabase/client";
import type { LngLat } from "@/utils/polyline";
import type { RoadMode } from "@/content/greatRoads";
import { type ScenicRoad, setNominatedRoads } from "@/utils/scenicRoads";

export interface RoadNomination {
  id: string;
  /** Only present on the row returned by submit; never selected for display —
   *  nothing downstream needs the contributor's id. */
  user_id?: string;
  name: string;
  city: string | null;
  state: string | null;
  lat: number;
  lng: number;
  mode: RoadMode;
  why: string | null;
  created_at: string;
  /** Resolved road polyline ([lng, lat] pairs) written at approval; null for
   *  rows approved before geometry resolution existed. */
  samples?: LngLat[] | null;
  /** Aggregated upvotes (excluding the implicit nominator vote). */
  votes?: number;
}

export async function submitRoadNomination(args: {
  name: string;
  coord: LngLat;
  mode: RoadMode;
  city?: string;
  state?: string;
  why?: string;
}): Promise<RoadNomination> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Sign in to nominate a road");

  const { data, error } = await supabase
    .from("nominated_roads")
    .insert({
      user_id: user.id,
      name: args.name.trim().slice(0, 80),
      city: args.city?.trim().slice(0, 80) || null,
      state: args.state?.trim().slice(0, 40) || null,
      lng: args.coord[0],
      lat: args.coord[1],
      mode: args.mode,
      why: args.why?.trim().slice(0, 240) || null,
    })
    .select()
    .single();
  if (error) throw error;
  const nom = data as unknown as RoadNomination;

  // Fire-and-forget: email Morgan to review this nomination. A mail hiccup must
  // never fail the nomination, so we don't await or surface errors.
  void supabase.functions
    .invoke("notify-road-nomination", {
      body: {
        id: nom.id,
        name: nom.name,
        why: nom.why,
        city: nom.city,
        state: nom.state,
        lat: nom.lat,
        lng: nom.lng,
        mode: nom.mode,
        nominator: user.email ?? user.id,
      },
    })
    .catch(() => {});

  return nom;
}

export async function upvoteRoadNomination(nominationId: string): Promise<void> {
  const {
    data: { user },
  } = await supabase.auth.getUser();
  if (!user) throw new Error("Sign in to upvote");
  const { error } = await supabase
    .from("road_votes")
    .insert({ nomination_id: nominationId, user_id: user.id });
  if (error && !/duplicate/i.test(error.message)) throw error;
}

// Circuit breaker: this optional community feature queries a table that may not
// have the `status` column in every deployment (migration drift). Rather than
// 400 on EVERY route generation and spam the console, we disable the query for
// the rest of the session after the first failure. Re-enabled on reload (so it
// recovers once the migration is applied).
let nominationsDisabled = false;

/** Fetch road nominations in a bbox, with vote counts. */
export async function fetchRoadNominationsNear(
  bbox: { minLng: number; minLat: number; maxLng: number; maxLat: number },
): Promise<RoadNomination[]> {
  if (nominationsDisabled) return [];
  const { data, error } = await supabase
    .from("nominated_roads")
    .select("id, name, city, state, lat, lng, mode, why, samples, created_at")
    // Only roads Morgan has reviewed surface on routes. Pending/rejected
    // nominations stay invisible until approved in Supabase.
    .eq("status", "approved")
    .gte("lat", bbox.minLat)
    .lte("lat", bbox.maxLat)
    .gte("lng", bbox.minLng)
    .lte("lng", bbox.maxLng)
    .limit(200);
  if (error) {
    // A 400 here means the table is missing a column (e.g. `status`) in this
    // project — stop querying for the session so we don't fail on every route.
    nominationsDisabled = true;
    return [];
  }
  const noms = (data ?? []) as RoadNomination[];
  if (noms.length === 0) return [];
  const { data: votes } = await supabase
    .from("road_votes")
    .select("nomination_id")
    .in("nomination_id", noms.map((n) => n.id));
  const counts = new Map<string, number>();
  for (const v of (votes ?? []) as { nomination_id: string }[]) {
    counts.set(v.nomination_id, (counts.get(v.nomination_id) ?? 0) + 1);
  }
  return noms.map((n) => ({ ...n, votes: counts.get(n.id) ?? 0 }));
}

// ─────────── Region cache + matcher feed ───────────
// The curated-road matcher (scenicRoads.ts) is synchronous, so we fetch the
// region's nominations ahead of routing and hand them to the matcher via
// setNominatedRoads(). Empty list = the matcher behaves exactly as before.

let cacheKey = "";

/** Shape a nomination as a ScenicRoad the matcher understands. Uses the
 *  resolved `samples` polyline when the row has one; otherwise the single
 *  nomination point, exactly as before. */
export function asScenicRoad(n: RoadNomination): ScenicRoad {
  const samples: LngLat[] =
    Array.isArray(n.samples) && n.samples.length > 0 ? n.samples : [[n.lng, n.lat]];
  return {
    id: `nom:${n.id}`,
    name: n.name,
    region: [n.city, n.state].filter(Boolean).join(", ") || "Nominated",
    samples,
    maxOffsetKm: 15,
    modes: [n.mode],
    blurb: n.why ?? undefined,
  };
}

export async function prefetchRoadNominationsForRoutes(allCoords: LngLat[]): Promise<void> {
  if (allCoords.length < 2) return;
  let minLng = Infinity, maxLng = -Infinity, minLat = Infinity, maxLat = -Infinity;
  for (const [lng, lat] of allCoords) {
    if (lng < minLng) minLng = lng;
    if (lng > maxLng) maxLng = lng;
    if (lat < minLat) minLat = lat;
    if (lat > maxLat) maxLat = lat;
  }
  const pad = 0.02;
  const key = `${minLng.toFixed(2)},${minLat.toFixed(2)},${maxLng.toFixed(2)},${maxLat.toFixed(2)}`;
  if (key === cacheKey) return;
  cacheKey = key;
  const noms = await fetchRoadNominationsNear({
    minLng: minLng - pad, maxLng: maxLng + pad,
    minLat: minLat - pad, maxLat: maxLat + pad,
  });
  setNominatedRoads(noms.map(asScenicRoad));
}
